import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { Table as TableComponent } from '@src/shared/ui/components/TableQuery';
import { dayjs } from '@uhg-abyss/web/tools/dayjs';
import { Badge } from '@uhg-abyss/web/ui/Badge';
import { Link } from '@uhg-abyss/web/ui/Link';
import { Text } from '@uhg-abyss/web/ui/Text';
import { Tooltip } from '@uhg-abyss/web/ui/Tooltip';
import { orderBy } from 'lodash';
import React, { useMemo } from 'react';

import configuration from './includes/configuration.json';

/**
 * Table: Report Log
 *
 * Displays a list of report history.
 *
 * @param assets
 * @param error
 * @param headerLeft
 * @param isLoading
 * @param refetch
 * @param requestArgs
 * @param requestFunction
 * @param requestKey
 * @param rows
 * @param totalPages
 * @param totalRecords
 * @returns {Element}
 * @constructor
 */
export function Table({
  assets,
  error,
  headerLeft,
  isLoading,
  refetch,
  requestArgs,
  requestFunction,
  requestKey,
  rows,
  totalPages,
  totalRecords,
}) {
  /**
   * renderCellDate
   *
   * Displays the date of the report within a cell.
   *
   * @param cell
   * @param row
   * @returns {Element}
   */
  const renderCellDate = ({ cell, row }) => {
    return <Link href={row?.original?.uploadUrl}>{dayjs(cell.value).format('MM/DD/YYYY - HH:mm:ss')}</Link>;
  };

  /**
   * renderCellAttribute
   *
   * Displays the attribute of the report within a cell.
   *
   * @param row
   * @param cell
   * @returns {React.JSX.Element|*}
   */
  const renderCellAttribute = ({ cell, row }) => {
    return <Text css={{ textTransform: 'uppercase' }}>{cell?.value}</Text>;
  };

  /**
   * renderCellSource
   *
   * Displays the tag of the report within a cell.
   *
   * @param row
   * @param cell
   * @returns {React.JSX.Element|*}
   */
  const renderCellSource = ({ cell, row }) => {
    const fullSourceName = assets?.ListEimpSources?.data
      ?.find(({ codeDesc, codeId }) => {
        if (codeId === cell?.value) {
          const codeDescription = codeDesc.split(';');
          return codeDescription?.[0]?.trim();
        }
        return null;
      })
      ?.codeDesc?.split(';')?.[0]
      ?.trim();

    return (
      <Tooltip align="start" content={fullSourceName} placement="auto" position="right">
        <Text>{cell?.value}</Text>
      </Tooltip>
    );
  };

  /**
   * renderCellTotals
   *
   * Displays the name of the report within a cell.
   *
   * @param row
   * @param cell
   * @returns {React.JSX.Element|*}
   */
  const renderCellTotals = ({ cell, row }) => {
    return <Text>{cell?.value}</Text>;
  };

  /**
   * renderCellStatus
   *
   * Displays the status of the report within a cell.
   *
   * @param row
   * @param cell
   * @returns {React.JSX.Element|*}
   */
  const renderCellStatus = ({ cell, row }) => {
    if (cell.value === 'EXTRACTED') {
      return <Badge outline>{cell?.value}</Badge>;
    }

    if (cell.value === 'SCHEDULED') {
      return (
        <Badge outline variant="info">
          {cell?.value}
        </Badge>
      );
    }

    if (cell.value === 'ERROR') {
      return (
        <Badge outline variant="error">
          {cell?.value}
        </Badge>
      );
    }

    if (cell.value === 'RUNNING') {
      return (
        <Badge outline variant="warning">
          {cell?.value}
        </Badge>
      );
    }

    return (
      <Badge outline variant="neutral">
        {cell?.value}
      </Badge>
    );
  };

  /**
   * Columns for table.
   */
  const columns = useMemo(() => {
    return orderBy(configuration?.initialColumns, ['order'], ['asc']).map((item) => {
      const column = item;

      if (column.Header === 'Created Date') {
        column.Cell = renderCellDate;
      }

      if (column.Header === 'Attribute') {
        column.Cell = renderCellAttribute;
      }

      if (column.Header === 'Source') {
        column.Cell = renderCellSource;
      }

      if (column.Header === 'Total Exported Records') {
        column.Cell = renderCellTotals;
      }

      if (column.Header === 'Report Status') {
        column.Cell = renderCellStatus;
      }

      return column;
    });
  }, [assets]);

  return (
    <ErrorHandler location="src/pages/private/Dashboards/SourceReportsDashboard/components/ReportLog/components/Table/Table.jsx">
      <TableComponent
        {...{
          columns,
          configuration,
          error,
          headerLeft,
          isLoading,
          refetch,
          requestArgs,
          requestFunction,
          requestKey,
          rows,
          totalPages,
          totalRecords,
        }}
      />
    </ErrorHandler>
  );
}
